import { useState } from "react";
import { App, Button, DatePicker, Form, InputNumber, Popconfirm, Select, Space, Table, Tag, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { FlashSale } from "@glow-price/shared";
import { ENTITY_STATUSES, formatPriceCNY } from "@glow-price/shared";

import { channelProductsApi, flashSalesApi } from "../api/resources";
import { CrudDrawer } from "../components/CrudDrawer";

const STATUS_OPTIONS = Object.values(ENTITY_STATUSES).map((v) => ({ value: v, label: v }));

type FormValues = {
  channelProductId?: string;
  flashPrice?: number;
  stockRemaining?: number | null;
  status?: string;
  range?: [dayjs.Dayjs, dayjs.Dayjs];
};

export function FlashSalesPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<FlashSale | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const cpQuery = useQuery({
    queryKey: ["channel-products", "all"],
    queryFn: () => channelProductsApi.list({ page: 1, pageSize: 500 }),
  });
  const cpOptions = (cpQuery.data?.items ?? []).map((cp) => ({
    value: cp.id,
    label: `${cp.channelProductName} (${cp.channelProductId})`,
  }));

  const list = useQuery({
    queryKey: ["flash-sales", page],
    queryFn: () => flashSalesApi.list({ page, pageSize: 20 }),
  });

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const startsAt = values.range?.[0]?.toISOString();
      const endsAt = values.range?.[1]?.toISOString();
      const body = {
        channelProductId: values.channelProductId,
        startsAt,
        endsAt,
        flashPrice: values.flashPrice,
        stockRemaining: values.stockRemaining ?? null,
        status: values.status,
      };
      if (editing) return flashSalesApi.update(editing.id, body as Partial<FlashSale>);
      return flashSalesApi.create(body as Partial<FlashSale>);
    },
    onSuccess: () => {
      message.success("已保存");
      qc.invalidateQueries({ queryKey: ["flash-sales"] });
      setDrawerOpen(false);
      setEditing(null);
    },
    onError: (err: Error) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => flashSalesApi.remove(id),
    onSuccess: () => {
      message.success("已删除");
      qc.invalidateQueries({ queryKey: ["flash-sales"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          限时秒杀
        </Typography.Title>
        <Button
          type="primary"
          onClick={() => {
            setEditing(null);
            setDrawerOpen(true);
          }}
        >
          新建秒杀
        </Button>
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        给指定渠道商品配置一段秒杀窗口和秒杀价。当前用户端按 starts_at/ends_at
        判断 active / upcoming / ended。
      </Typography.Paragraph>

      <Table<FlashSale>
        rowKey="id"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: 20,
          total: list.data?.total ?? 0,
          onChange: (p) => setPage(p),
          showSizeChanger: false,
        }}
        columns={[
          {
            title: "渠道商品",
            dataIndex: "channelProductId",
            render: (v) => cpOptions.find((o) => o.value === v)?.label ?? v,
          },
          {
            title: "开抢 → 结束",
            render: (_, row) =>
              `${dayjs(row.startsAt).format("MM-DD HH:mm")} → ${dayjs(row.endsAt).format("MM-DD HH:mm")}`,
            width: 220,
          },
          {
            title: "秒杀价",
            dataIndex: "flashPrice",
            render: (v) => formatPriceCNY(v),
            width: 100,
          },
          {
            title: "库存",
            dataIndex: "stockRemaining",
            render: (v) => (v == null ? "不限" : v),
            width: 80,
          },
          {
            title: "阶段",
            render: (_, row) => {
              const now = Date.now();
              const s = new Date(row.startsAt).getTime();
              const e = new Date(row.endsAt).getTime();
              if (now < s) return <Tag color="blue">upcoming</Tag>;
              if (now < e) return <Tag color="red">active</Tag>;
              return <Tag>ended</Tag>;
            },
            width: 100,
          },
          { title: "状态", dataIndex: "status", width: 90 },
          {
            title: "操作",
            width: 160,
            render: (_, row) => (
              <Space>
                <Button
                  type="link"
                  onClick={() => {
                    setEditing(row);
                    setDrawerOpen(true);
                  }}
                >
                  编辑
                </Button>
                <Popconfirm title="确认删除？" onConfirm={() => deleteMutation.mutate(row.id)}>
                  <Button type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <CrudDrawer<FormValues>
        open={drawerOpen}
        title={editing ? "编辑秒杀" : "新建秒杀"}
        initialValues={
          (editing
            ? {
                channelProductId: editing.channelProductId,
                flashPrice: editing.flashPrice,
                stockRemaining: editing.stockRemaining,
                status: editing.status,
                range: [dayjs(editing.startsAt), dayjs(editing.endsAt)],
              }
            : {
                status: ENTITY_STATUSES.ACTIVE,
                range: [dayjs(), dayjs().add(2, "hour")],
              }) as FormValues
        }
        loading={saveMutation.isPending}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        onSubmit={(values) => saveMutation.mutateAsync(values)}
      >
        {() => (
          <>
            <Form.Item name="channelProductId" label="渠道商品" rules={[{ required: true }]}>
              <Select
                options={cpOptions}
                showSearch
                filterOption={(input, option) =>
                  (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="range" label="开抢窗口" rules={[{ required: true }]}>
              <DatePicker.RangePicker showTime style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="flashPrice" label="秒杀价" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="stockRemaining" label="库存（留空 = 不限）">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </>
        )}
      </CrudDrawer>
    </div>
  );
}
